import { useState, useContext, useCallback, ChangeEvent } from 'react';
import { useMutation } from '@apollo/react-hooks';
import { CREATE_COINFLIP_GAME } from '@graphql/Mutations';
import readyAudio from '@assets/audio/ready.mp3';
import errorAudio from '@assets/audio/error.mp3';
import useSound from 'use-sound';

import useReduxState from '@hooks/useReduxState';
import { SocketContext } from '@context/socket';

import styles from './CreateGame.module.scss';
import cn from 'classnames';

import padlockSvg from '@assets/svg/padlock.svg';
import coinAnaSvg from '@assets/svg/coin-ana.svg';
import coinDuelSvg from '@assets/svg/coin-duel.svg';
import { toastErrorMessage, toastSuccessMessage } from '@utils/utils';
import BetInput from './bet-input';

const MIN_BET_AMOUNT = 1;

interface IFormState {
  amount: string;
  isCreatingGame: boolean;
}

const INITIAL_FORM_STATE: IFormState = {
  amount: '',
  isCreatingGame: false,
};

export default function CreateGame(): JSX.Element {
  const socket = useContext(SocketContext);

  const [user, setData] = useReduxState((state) => state.userData);
  const [playReadySound] = useSound(readyAudio, { interrupt: true });
  const [playErrorSound] = useSound(errorAudio, { interrupt: true });
  const [{ amount, isCreatingGame }, setFormState] = useState<IFormState>(INITIAL_FORM_STATE);
  const [side, setSide] = useState<'duel' | 'ana'>('duel');
  const [isPrivateGame, setIsPrivateGame] = useState<boolean>(false);

  const [createCoinflipGame] = useMutation(CREATE_COINFLIP_GAME, {
    onCompleted: (data) => {
      setData({
        type: 'ADD_COINFLIP_GAME',
        arg: {
          game: data.createCoinflipGame,
        },
      });

      setData({
        type: 'USER_UPDATE_BALANCE',
        arg: {
          balance: +amount,
          type: 'substract',
        },
      });

      /* Resets form state */
      setFormState(INITIAL_FORM_STATE);

      socket.emit('New-Coinflip', {
        gameUid: (data as any).createCoinflipGame.uid,
        is_private: (data as any).createCoinflipGame.game_metadata.is_private,
      });

      playReadySound();
      toastSuccessMessage('Game Successfully Created');
    },
    onError: (e) => {
      playErrorSound();
      saveInForm('isCreatingGame', false);
      toastErrorMessage(e.message);
    },
  });

  /* Handlers */

  /**
   * Handles the form changes and updates the form state
   * @param {string} key  The key of the form state to update
   * @param {string | boolean} value The value to update the form state with
   * @return {void} - Updates the form state and doesn't return anything
   */
  const saveInForm = (key: string, value: string | boolean): void =>
    setFormState((prevState) => ({
      ...prevState,
      [key]: value,
    }));

  const handleBetChange = (e: ChangeEvent<HTMLInputElement>) => {
    /* To avoid negative values */
    if (+e.target.value < 0) return;

    saveInForm('amount', e.target.value);
  };

  const handleClickPrivate = (): void => setIsPrivateGame(!isPrivateGame);

  const duplicateBet = (): void => saveInForm('amount', ((+amount <= 0 ? MIN_BET_AMOUNT : +amount) * 2).toFixed(2).toString());

  const divideBetInHalf = (): void => saveInForm('amount', ((+amount <= 0 ? MIN_BET_AMOUNT : +amount) / 2).toFixed(2).toString());

  const updateBet = (value: string): void => saveInForm('amount', value);

  const handleCreateGame = useCallback(async () => {
    if (!isCreatingGame) {
      saveInForm('isCreatingGame', true);

      await createCoinflipGame({
        variables: {
          stake: parseFloat(amount),
          userPick: side == 'duel' ? 'heads' : 'tails',
          is_private: isPrivateGame,
          userId: user.id,
        },
      });
    }
  }, [amount, side, isCreatingGame]);

  /* ClassNames */

  const containerClasses: string = cn(styles.container, {
    [styles[`container-side-${side}`]]: side,
  });

  return (
    <>
      {/* Create/New Game Section */}
      <div className={containerClasses}>
        <section className={styles.coins}>
          <div className={styles.title}>
            <h3>
              Choose
              <br />
              your side
            </h3>
          </div>

          <div className={styles.sides}>
            <div
              className={cn(styles.side, {
                [styles.side__active]: side == 'duel',
              })}
              onClick={() => {
                setSide('duel');
              }}
            >
              <div
                className={cn(styles.circle, styles.duel, {
                  [styles['circle-duel']]: side === 'duel',
                })}
              >
                <img src={coinDuelSvg} alt='duel' />
              </div>
              <label>DUEL</label>
            </div>

            <div
              className={cn(styles.side, {
                [styles.side__active]: side == 'ana',
              })}
              onClick={() => {
                setSide('ana');
              }}
            >
              <div
                className={cn(styles.circle, styles.ana, {
                  [styles['circle-ana']]: side === 'ana',
                })}
              >
                <img src={coinAnaSvg} alt='ana' />
              </div>
              <label>ANA</label>
            </div>
          </div>
        </section>

        <div className={styles.footer}>
          <section className={styles.group}>
            <BetInput value={amount} onChange={handleBetChange} {...{ divideBetInHalf, duplicateBet, updateBet }} />

            <div className={styles.private} onClick={handleClickPrivate}>
              <label>Private</label>
              <div
                className={cn(styles.button, {
                  [styles.button__active]: isPrivateGame,
                })}
              >
                <input type='checkbox' name='privateGame' />
                <img src={padlockSvg} />
              </div>
            </div>
          </section>

          <button className={styles.createButton} onClick={handleCreateGame}>
            Create Game
          </button>
        </div>
      </div>
    </>
  );
}
